import React, { useEffect, useRef, useState } from "react";
import {
  apiErrorMessage, apiGet, createClientUpdate, getRollForwardProposal,
  listify, rollForwardTask,
} from "../../api";
import RollForwardPrompt from "./RollForwardPrompt";

// The requests behind RollForwardPrompt, shared by the Tasks grid (Status ->
// Completed) and the task page (the completion checkmark and the standalone
// "Roll Forward to Next Year" button).
//
// `task` must already be completed -- the server refuses to roll an open one.
// `employees` is optional; without it the active staff list is fetched here.
// onRolled(newTask, { archivedSource }) runs after a successful roll.
export default function RollForwardFlow({ task, employees, onRolled, onClose }) {
  const [proposal, setProposal] = useState(null);
  const [staff, setStaff] = useState(employees || []);
  const [busy, setBusy] = useState(false);
  const [unapproved, setUnapproved] = useState(null);
  const [error, setError] = useState("");
  // The note is posted before the roll. If the roll then fails and the person
  // tries again, the same note must not be posted twice.
  const notePosted = useRef("");
  // What the person confirmed, kept so a manager's "Roll forward anyway"
  // resends the same edits rather than the engine's defaults.
  const lastConfirm = useRef(null);

  useEffect(() => {
    let live = true;
    getRollForwardProposal(task.id)
      .then((p) => { if (live) setProposal(p); })
      .catch((e) => {
        if (live) setProposal({ eligible: false, reason: apiErrorMessage(e, "") });
      });
    return () => { live = false; };
  }, [task.id]);

  useEffect(() => {
    if (employees) { setStaff(employees); return; }
    let live = true;
    apiGet("/api/employees/")
      .then((rows) => {
        if (!live) return;
        setStaff(listify(rows)
          .filter((e) => e.is_active !== false)
          .map((e) => ({
            id: e.id,
            name: e.full_name ||
              `${e.first_name || ""} ${e.last_name || ""}`.trim() ||
              e.username || "Unknown",
          })));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [employees]);

  const clientId = typeof task.client === "object" ? task.client?.id : task.client;

  const confirm = async ({ overrides, archiveSource, note }, overrideUnapproved = false) => {
    lastConfirm.current = { overrides, archiveSource, note };
    setBusy(true);
    setError("");
    try {
      if (note && note !== notePosted.current && clientId) {
        await createClientUpdate({ client: clientId, content: note });
        notePosted.current = note;
      }
      const next = await rollForwardTask(task.id, {
        overrides, archive_source: archiveSource,
        ...(overrideUnapproved ? { override_unapproved: true } : {}),
      });
      onRolled(next, { archivedSource: archiveSource });
    } catch (e) {
      const body = e?.response?.data;
      if (body?.code === "unapproved_time") {
        setUnapproved({ ...body.unapproved, can_override: Boolean(body.can_override) });
      } else {
        setError(apiErrorMessage(e, "Roll-forward failed."));
      }
    } finally {
      setBusy(false);
    }
  };

  const activeStaff = (staff || []).filter((e) => e.is_active !== false);

  return (
    <RollForwardPrompt
      clientName={task.client_name || task.client?.name || "this client"}
      year={task.fiscal_year}
      nextYear={(task.fiscal_year || 0) + 1}
      proposal={proposal}
      employees={activeStaff}
      busy={busy}
      unapproved={unapproved}
      error={error}
      onConfirm={confirm}
      onOverride={() => confirm(lastConfirm.current || {}, true)}
      onDismiss={onClose}
    />
  );
}
